import {
  AlignmentType,
  Document,
  Header,
  HorizontalPositionRelativeFrom,
  ImageRun,
  Paragraph,
  Table,
  TableCell,
  TableRow,
} from 'docx'
import sscLogo from '@/assets/logo3.png'
import tlaxcalaLogo from '@/assets/logo2.png'
import watermarkLogo from '@/assets/watermark.png'
import BaseHeader from '../BaseHeader'
import BaseText from '../BaseText'
import BaseFooter from '../BaseFooter'

const LETTER_PAGE_SIZE = {
  width: 12240,
  height: 15840,
  orientation: 'portrait',
}

class ConstanciaNoAdeudo {
  constructor({ nombre = '', rfc = '', fecha = '', area = '', fechaIngreso = '', fechaBaja = '' } = {}) {
    this.nombre = nombre
    this.rfc = rfc
    this.fecha = fecha
    this.area = area
    this.fechaIngreso = fechaIngreso
    this.fechaBaja = fechaBaja
  }
  formatearFechaLectura(fecha, prefijo = '') {
    if (!fecha) return ''

    if (fecha.includes('/')) {
      const [day, month, year] = fecha.split('/')
      fecha = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
    const [year, month, day] = fecha.split('-')
    const meses = [
      '',
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ]
    return `${prefijo}${parseInt(day)} del mes de ${meses[parseInt(month)]} del año ${year}`
  }

  get LineaEnBlanco() {
    return new Paragraph({
      children: [],
      spacing: { after: 800 },
    })
  }

  get NumeroOficio() {
    const NUMERO_OFICIO = 'SSC/DA/****/2025.'

    return new Paragraph({
      children: [new BaseText('OFICIO NÚM: ', { bold: true }).value, new BaseText(NUMERO_OFICIO).value],
      alignment: 'end',
    })
  }

  get Asunto() {
    const ASUNTO = 'CONSTANCIA DE NO ADEUDO.'
    return new Paragraph({
      children: [new BaseText('ASUNTO: ', { bold: true }).value, new BaseText(ASUNTO).value],
      alignment: 'end',
    })
  }

  get Fecha() {
    const [year, month, day] = this.fecha ? this.fecha.split('-') : ['2025', '01', '**']
    const meses = [
      '',
      'ENERO',
      'FEBRERO',
      'MARZO',
      'ABRIL',
      'MAYO',
      'JUNIO',
      'JULIO',
      'AGOSTO',
      'SEPTIEMBRE',
      'OCTUBRE',
      'NOVIEMBRE',
      'DICIEMBRE',
    ]
    return new Paragraph({
      children: [
        new BaseText('TLAXCALA, TLAX.,A ', { bold: false }).value,
        new BaseText(day || '**').value,
        new BaseText(' DE ', { bold: false }).value,
        new BaseText(meses[parseInt(month, 10)] || '******').value,
        new BaseText(' DEL ' + (year || '2025'), { bold: false }).value,
      ],
      alignment: 'end',
    })
  }

  get Saludo() {
    return new Paragraph({
      children: [
        new BaseText('A QUIEN CORRESPONDA:', {
          bold: true,
        }).value,
      ],
      alignment: 'start',
    })
  }

  get Cuerpo() {
    return [
      new Paragraph({
        children: [
          new BaseText(
            'En mi carácter de Directora Administrativa de la Secretaría de Seguridad Ciudadana, con domicilio en Calle Xicohtencatl No. 13 Esquina con Lardizabal, Colonia Centro, Tlaxcala,Tlax., hago constar que la C. '
          ).value,
          new BaseText(this.nombre, { underline: true }).value,
          new BaseText(', con R.F.C. ').value,
          new BaseText(this.rfc, { underline: true }).value,
          new BaseText(', quien laboró ').value,
          new BaseText(this.formatearFechaLectura(this.fechaIngreso, 'a partir del dia '), { underline: true }).value,
          new BaseText(', ').value,
          new BaseText(this.formatearFechaLectura(this.fechaBaja, 'al dia '), { underline: true }).value,
          new BaseText(', con el puesto de ').value,
          new BaseText(this.area, { underline: true }).value,
          new BaseText(', NO TIENE ADEUDO ALGUNO en el periodo mencionado.').value,
        ],
        alignment: AlignmentType.JUSTIFIED,
      }),
      this.LineaEnBlanco,
      new Paragraph({
        children: [
          new BaseText(
            'Por lo que se extiende el presente documento a petición de la interesada para fines que convengan.'
          ).value,
        ],
      }),
    ]
  }

  get Firma() {
    return [
      new Paragraph({
        children: [new BaseText('ATENTAMENTE', { bold: true }).value],
        alignment: 'center',
      }),
      this.LineaEnBlanco,
      this.LineaEnBlanco,
      new Paragraph({
        children: [new BaseText('DIRECTORA ADMINISTRATIVA DE LA', { bold: true }).value],
        alignment: 'center',
      }),
      new Paragraph({
        children: [new BaseText('SECRETARÍA DE SEGURIDAD CIUDADANA', { bold: true }).value],
        alignment: 'center',
      }),
    ]
  }

  async MarcaAgua() {
    const watermarkBuffer = await fetch(watermarkLogo)
      .then((r) => r.blob())
      .then((b) => b.arrayBuffer())

    return new Paragraph({
      children: [
        new ImageRun({
          data: watermarkBuffer,
          type: 'png',
          transformation: {
            width: 300,
            height: 600,
          },
          floating: {
            horizontalPosition: {
              align: 'left',
            },
            verticalPosition: {
              align: 'center',
            },
            behindDocument: true,
          },
          transparency: 0.18,
        }),
      ],
    })
  }

  async generate() {
    try {
      const sscLogoBuffer = await fetch(sscLogo)
        .then((r) => r.blob())
        .then((b) => b.arrayBuffer())

      const tlaxcalaLogoBuffer = await fetch(tlaxcalaLogo)
        .then((r) => r.blob())
        .then((b) => b.arrayBuffer())

      const marcaAgua = await this.MarcaAgua()

      const footer = await BaseFooter.generate()

      const document = new Document({
        sections: [
          {
            properties: { page: { size: { ...LETTER_PAGE_SIZE } } },
            headers: { default: new BaseHeader(sscLogoBuffer, tlaxcalaLogoBuffer) },
            children: [
              marcaAgua,
              this.NumeroOficio,
              this.Asunto,
              this.Fecha,
              this.LineaEnBlanco,
              this.Saludo,
              this.LineaEnBlanco,
              ...this.Cuerpo,
              this.LineaEnBlanco,
              ...this.Firma,
            ],
            footers: { default: footer },
          },
        ],
      })

      return document
    } catch (error) {
      console.error('Error generating ConstanciaNoAdeudo document:', error)
      throw error
    }
  }
}

export default ConstanciaNoAdeudo
